import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { orderApi } from '../api/orderApi';
import { marketDataApi } from '../api/marketDataApi';
import { schwabApi } from '../api/schwabApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faSync,
  faPause,
  faTimes,
  faCheck,
  faShieldAlt,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { useRisk } from '../contexts/RiskContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrderPresets, priceFromMidOffset, midFromQuote } from '../contexts/OrderPresetsContext';

const VENUE_STORAGE_KEY = 'lt-order-venue';
const SCHWAB_ACCOUNT_STORAGE_KEY = 'lt-order-schwab-account';

const TERMINAL_STATUSES = new Set([
  'filled',
  'canceled',
  'cancelled',
  'expired',
  'rejected',
  'suspended',
]);

const SCHWAB_ORDER_TYPE = {
  market: 'MARKET',
  limit: 'LIMIT',
  stop: 'STOP',
  stop_limit: 'STOP_LIMIT',
};

const SCHWAB_DURATION = {
  day: 'DAY',
  gtc: 'GOOD_TILL_CANCEL',
  ioc: 'IMMEDIATE_OR_CANCEL',
  fok: 'FILL_OR_KILL',
};

function maskAccount(num) {
  if (!num) return '—';
  const s = String(num);
  return s.length <= 4 ? s : `••••${s.slice(-4)}`;
}

function mapAlpacaToSchwabPayload(orderData, accountNumber) {
  const instruction = orderData.side === 'sell' ? 'SELL' : 'BUY';
  const payload = {
    accountNumber: String(accountNumber),
    symbol: orderData.symbol,
    instruction,
    quantity: Number(orderData.qty),
    orderType: SCHWAB_ORDER_TYPE[orderData.type] || 'MARKET',
    duration: SCHWAB_DURATION[orderData.timeInForce] || 'DAY',
    session: orderData.extendedHours ? 'SEAMLESS' : 'NORMAL',
    assetType: 'EQUITY',
  };
  if (orderData.limitPrice != null) payload.price = orderData.limitPrice;
  if (orderData.stopPrice != null) payload.stopPrice = orderData.stopPrice;
  return payload;
}

const normalizeOrder = (raw, extras = {}) => {
  if (!raw) return null;
  return {
    id: raw.id ?? raw.orderId ?? null,
    clientOrderId: raw.clientOrderId || raw.client_order_id,
    symbol: raw.symbol,
    qty: raw.qty != null ? parseFloat(raw.qty) : raw.quantity != null ? parseFloat(raw.quantity) : null,
    notional: raw.notional != null ? parseFloat(raw.notional) : null,
    side: raw.side || raw.instruction,
    type: raw.type || raw.orderType,
    timeInForce: raw.timeInForce || raw.time_in_force || raw.duration,
    status: raw.status || (raw.orderId ? 'submitted' : null),
    filledQty: parseFloat(raw.filledQty ?? raw.filled_qty ?? 0),
    filledAvgPrice: parseFloat(raw.filledAvgPrice ?? raw.filled_avg_price ?? 0),
    submittedAt: raw.submittedAt || raw.submitted_at,
    venue: extras.venue || raw.venue || 'alpaca',
    accountNumber: extras.accountNumber || raw.accountNumber || null,
  };
};

const formatStatus = (status) => {
  if (!status) return 'Unknown';
  return String(status).replace(/_/g, ' ');
};

const OrderForm = ({
  symbol: initialSymbol = '',
  onOrderSubmitted,
  initialSide = 'buy',
  lockSymbol = false,
  hideQuoteStrip = false,
  defaultAutoRefresh = false,
  variant = 'default',
}) => {
  const { checkOrder } = useRisk();
  const { accessToken } = useAuth();
  const {
    presets,
    activeId,
    activePreset,
    setActiveId,
    savePreset,
    removePreset,
  } = useOrderPresets();
  const [riskPrompt, setRiskPrompt] = useState(null); // { blocks, warnings, orderData }
  const [presetNameDraft, setPresetNameDraft] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [symbol, setSymbol] = useState(initialSymbol);
  const [qty, setQty] = useState('');
  const [notional, setNotional] = useState(''); // Dollar amount for fractional shares
  const [orderBy, setOrderBy] = useState('qty'); // 'qty' or 'notional'
  const [side, setSide] = useState(initialSide === 'sell' ? 'sell' : 'buy');
  const [orderType, setOrderType] = useState('market');
  const [timeInForce, setTimeInForce] = useState('day');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [extendedHours, setExtendedHours] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(defaultAutoRefresh);
  const [executionVenue, setExecutionVenue] = useState(() => {
    try {
      return localStorage.getItem(VENUE_STORAGE_KEY) === 'schwab' ? 'schwab' : 'alpaca';
    } catch {
      return 'alpaca';
    }
  });
  const [schwabConnected, setSchwabConnected] = useState(false);
  const [schwabAccounts, setSchwabAccounts] = useState([]);
  const [schwabAccountsLoading, setSchwabAccountsLoading] = useState(false);
  const [schwabAccountNumber, setSchwabAccountNumber] = useState(() => {
    try {
      return localStorage.getItem(SCHWAB_ACCOUNT_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const quoteTimeoutRef = useRef(null);
  const quoteIntervalRef = useRef(null);

  useEffect(() => {
    if (initialSymbol && initialSymbol !== symbol) {
      setSymbol(initialSymbol.toUpperCase());
    }
  }, [initialSymbol]);

  useEffect(() => {
    if (initialSide === 'buy' || initialSide === 'sell') {
      setSide(initialSide);
    }
  }, [initialSide]);

  // Load Schwab accounts when venue is Schwab (or on mount if already selected)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accessToken) return;
      try {
        setSchwabAccountsLoading(true);
        const statusRes = await schwabApi.getStatus().catch(() => ({ connected: false }));
        if (cancelled) return;
        const connected = Boolean(statusRes?.connected || statusRes?.data?.connected);
        setSchwabConnected(connected);
        if (!connected) {
          setSchwabAccounts([]);
          return;
        }
        const acctRes = await schwabApi.getAccounts(false);
        if (cancelled) return;
        const list = acctRes?.data || acctRes?.accounts || [];
        const accounts = Array.isArray(list) ? list : [];
        setSchwabAccounts(accounts);
        if (
          accounts.length &&
          !accounts.some((a) => String(a.accountNumber) === String(schwabAccountNumber))
        ) {
          const first = String(accounts[0].accountNumber || '');
          setSchwabAccountNumber(first);
          try {
            localStorage.setItem(SCHWAB_ACCOUNT_STORAGE_KEY, first);
          } catch {
            /* ignore */
          }
        }
      } catch {
        if (!cancelled) {
          setSchwabConnected(false);
          setSchwabAccounts([]);
        }
      } finally {
        if (!cancelled) setSchwabAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, executionVenue]);

  useEffect(() => {
    if (executionVenue === 'schwab' && orderBy === 'notional') {
      setOrderBy('qty');
      setNotional('');
    }
  }, [executionVenue, orderBy]);

  const applyPreset = (preset, { refreshLimit = true } = {}) => {
    if (!preset) return;
    setOrderBy(preset.orderBy === 'notional' ? 'notional' : 'qty');
    if (preset.orderBy === 'notional') {
      setNotional(preset.notional != null ? String(preset.notional) : '');
    } else if (preset.qty != null) {
      setQty(String(preset.qty));
    }
    setOrderType(preset.orderType || 'market');
    setTimeInForce(preset.timeInForce || 'day');
    setExtendedHours(Boolean(preset.extendedHours));
    if (refreshLimit && (preset.orderType === 'limit' || preset.orderType === 'stop_limit')) {
      const mid = midFromQuote(quote);
      const px = priceFromMidOffset(mid, preset.limitOffset);
      if (px != null) setLimitPrice(String(px));
    } else if (preset.orderType === 'market' || preset.orderType === 'stop') {
      // keep existing limit for stop; clear for market
      if (preset.orderType === 'market') setLimitPrice('');
    }
  };

  useEffect(() => {
    if (!activePreset) return;
    applyPreset(activePreset, { refreshLimit: true });
    // Only when active preset id changes — not on every quote tick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    if (!activePreset) return;
    if (activePreset.orderType !== 'limit' && activePreset.orderType !== 'stop_limit') return;
    if (activePreset.limitOffset == null) return;
    const mid = midFromQuote(quote);
    const px = priceFromMidOffset(mid, activePreset.limitOffset);
    if (px != null) setLimitPrice(String(px));
  }, [quote, activePreset]);

  const extractOrderError = (error) => {
    const data = error?.response?.data;
    if (data) {
      if (typeof data === 'string') return data;
      if (data.message) return data.message;
      if (data.error) return data.error;
      if (data.reason) return data.reason;
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors
          .map((e) => (typeof e === 'string' ? e : e.message || JSON.stringify(e)))
          .join('; ');
      }
    }
    return error?.message || 'Failed to submit order';
  };

  // Fetch quote when symbol changes (with debouncing) - only if auto-refresh is off
  useEffect(() => {
    if (autoRefresh) {
      // If auto-refresh is on, don't use debounced fetch
      return;
    }

    if (!symbol || symbol.length < 1) {
      setQuote(null);
      return;
    }

    // Clear existing timeout
    if (quoteTimeoutRef.current) {
      clearTimeout(quoteTimeoutRef.current);
    }

    // Debounce quote fetching to avoid too many API calls
    quoteTimeoutRef.current = setTimeout(async () => {
      try {
        setLoadingQuote(true);
        const response = await marketDataApi.getQuote(symbol.toUpperCase());
        if (response.success && response.data) {
          setQuote(response.data);
        } else {
          setQuote(null);
        }
      } catch (err) {
        console.error('Failed to load quote:', err);
        setQuote(null);
      } finally {
        setLoadingQuote(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (quoteTimeoutRef.current) {
        clearTimeout(quoteTimeoutRef.current);
      }
    };
  }, [symbol, autoRefresh]);

  // Auto-refresh quote every 1 second when enabled
  useEffect(() => {
    if (autoRefresh && symbol && symbol.length >= 1) {
      // Function to fetch quote
      const fetchQuote = async () => {
        try {
          setLoadingQuote(true);
          const response = await marketDataApi.getQuote(symbol.toUpperCase());
          if (response.success && response.data) {
            setQuote(response.data);
          } else {
            setQuote(null);
          }
        } catch (err) {
          console.error('Failed to load quote:', err);
          setQuote(null);
        } finally {
          setLoadingQuote(false);
        }
      };

      // Fetch immediately
      fetchQuote();

      // Set up interval to fetch every 1 second
      quoteIntervalRef.current = setInterval(() => {
        fetchQuote();
      }, 1000);

      return () => {
        if (quoteIntervalRef.current) {
          clearInterval(quoteIntervalRef.current);
        }
      };
    } else {
      // Clear interval if auto-refresh is disabled or no symbol
      if (quoteIntervalRef.current) {
        clearInterval(quoteIntervalRef.current);
        quoteIntervalRef.current = null;
      }
    }
  }, [autoRefresh, symbol]);

  // Auto-fill limit price when switching to limit order type
  useEffect(() => {
    if (orderType === 'limit' && !limitPrice && quote?.bidPrice && quote?.askPrice) {
      const midPrice = (quote.bidPrice + quote.askPrice) / 2;
      setLimitPrice(midPrice.toFixed(2));
    }
  }, [orderType, quote]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const placeOrder = async (orderData) => {
    try {
      setLoading(true);
      setMessage(null);

      let order;
      let notifyPayload;

      if (executionVenue === 'schwab') {
        if (!schwabConnected) {
          throw new Error('Connect Schwab on the Schwab page first');
        }
        if (!schwabAccountNumber) {
          throw new Error('Select a Schwab account');
        }
        if (orderData.notional != null || orderData.qty == null) {
          throw new Error('Schwab equity orders require share quantity (not dollar amount)');
        }
        const schwabPayload = mapAlpacaToSchwabPayload(orderData, schwabAccountNumber);
        const response = await schwabApi.placeOrder(schwabPayload);
        const result = response?.data || response;
        order = normalizeOrder(
          {
            ...result,
            symbol: orderData.symbol,
            qty: orderData.qty,
            side: orderData.side,
            type: orderData.type,
            timeInForce: orderData.timeInForce,
            status: result?.orderId ? 'submitted' : result?.status || 'submitted',
          },
          { venue: 'schwab', accountNumber: schwabAccountNumber }
        );
        notifyPayload = result;
      } else {
        const response = await orderApi.submitOrder(orderData);
        order = normalizeOrder(response.data, { venue: 'alpaca' });
        notifyPayload = response.data;
      }

      setConfirmation({
        type: 'success',
        order,
      });
      setMessage(null);

      setQty('');
      setNotional('');
      setLimitPrice('');
      setStopPrice('');

      if (onOrderSubmitted) {
        onOrderSubmitted(notifyPayload);
      }
    } catch (error) {
      setConfirmation({
        type: 'error',
        text: extractOrderError(error),
      });
      setMessage(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!symbol) {
      setMessage({ type: 'error', text: 'Symbol is required' });
      return;
    }

    if (executionVenue === 'schwab') {
      if (!schwabConnected) {
        setMessage({ type: 'error', text: 'Connect Schwab on the Schwab page first' });
        return;
      }
      if (!schwabAccountNumber) {
        setMessage({ type: 'error', text: 'Select a Schwab account' });
        return;
      }
      if (orderBy === 'notional') {
        setMessage({ type: 'error', text: 'Schwab ticket uses share quantity (not dollar amount)' });
        return;
      }
    }

    if (orderBy === 'qty' && !qty) {
      setMessage({ type: 'error', text: 'Quantity is required' });
      return;
    }

    if (orderBy === 'notional' && !notional) {
      setMessage({ type: 'error', text: 'Dollar amount is required' });
      return;
    }

    if (orderType === 'limit' && !limitPrice) {
      setMessage({ type: 'error', text: 'Limit price is required for limit orders' });
      return;
    }

    if ((orderType === 'stop' || orderType === 'stop_limit') && !stopPrice) {
      setMessage({ type: 'error', text: 'Stop price is required for stop orders' });
      return;
    }

    const orderData = {
      symbol: symbol.toUpperCase(),
      side,
      type: orderType,
      timeInForce,
    };

    if (orderBy === 'notional') {
      orderData.notional = parseFloat(notional);
    } else {
      orderData.qty = parseFloat(qty);
    }

    if (limitPrice) {
      orderData.limitPrice = parseFloat(limitPrice);
    }

    if (stopPrice) {
      orderData.stopPrice = parseFloat(stopPrice);
    }

    if (extendedHours) {
      orderData.extendedHours = true;
    }

    const totals = calculateTotal();
    const estimatedNotional =
      orderData.notional != null
        ? orderData.notional
        : totals?.subtotal != null
          ? totals.subtotal
          : null;

    // Alpaca risk gates apply to Alpaca only
    if (executionVenue !== 'alpaca') {
      await placeOrder(orderData);
      return;
    }

    try {
      setLoading(true);
      const risk = await checkOrder({
        ...orderData,
        estimatedNotional,
      });
      setLoading(false);

      if (risk.blocks?.length) {
        setMessage({
          type: 'error',
          text: `Risk block: ${risk.blocks.join(' · ')}`,
        });
        return;
      }

      if (risk.warnings?.length) {
        setRiskPrompt({
          warnings: risk.warnings,
          orderData,
        });
        return;
      }

      await placeOrder(orderData);
    } catch (err) {
      setLoading(false);
      setMessage({
        type: 'error',
        text: err.message || 'Risk check failed',
      });
    }
  };

  const midPrice = quote?.bidPrice && quote?.askPrice 
    ? (quote.bidPrice + quote.askPrice) / 2 
    : null;
  const spread = quote?.bidPrice && quote?.askPrice 
    ? quote.askPrice - quote.bidPrice 
    : null;

  // Calculate total cost of trade
  const calculateTotal = () => {
    const TRADE_FEE = 0.01; // $0.01 fee per trade
    
    if (orderBy === 'notional') {
      // For notional orders, the notional amount is the subtotal
      if (!notional || parseFloat(notional) <= 0) return null;
      const subtotal = parseFloat(notional);
      const total = subtotal + TRADE_FEE;
      return {
        quantity: null, // Not applicable for notional
        price: null,
        subtotal,
        fee: TRADE_FEE,
        total,
      };
    } else {
      // For qty orders
      if (!qty || parseFloat(qty) <= 0) return null;
      
      const quantity = parseFloat(qty);
      let price = null;
      
      if (orderType === 'limit' && limitPrice) {
        price = parseFloat(limitPrice);
      } else if (orderType === 'stop' && stopPrice) {
        price = parseFloat(stopPrice);
      } else if (orderType === 'stop_limit' && limitPrice) {
        price = parseFloat(limitPrice);
      } else if (orderType === 'market' && quote) {
        // For market orders, use ask price for buy, bid price for sell
        price = side === 'buy' ? quote.askPrice : quote.bidPrice;
      }
      
      if (!price || isNaN(price)) return null;
      
      const subtotal = quantity * price;
      const total = subtotal + TRADE_FEE;
      
      return {
        quantity,
        price,
        subtotal,
        fee: TRADE_FEE,
        total,
      };
    }
  };

  // Auto-hide inline messages after a few seconds
  useEffect(() => {
    if (!message) return undefined;
    const timeout = setTimeout(() => {
      setMessage(null);
    }, 8000);
    return () => clearTimeout(timeout);
  }, [message]);

  useEffect(() => {
    if (!confirmation) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setConfirmation(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmation]);

  useEffect(() => {
    const orderId = confirmation?.type === 'success' ? confirmation.order?.id : null;
    const status = confirmation?.order?.status;
    const venue = confirmation?.order?.venue;
    // Schwab place response is ack-only; no Alpaca-style status poll
    if (venue === 'schwab') return undefined;
    if (!orderId || TERMINAL_STATUSES.has(String(status || '').toLowerCase())) {
      return undefined;
    }

    let cancelled = false;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const response = await orderApi.getOrder(orderId);
        if (cancelled) return;
        const next = normalizeOrder(response.data || response, { venue: 'alpaca' });
        if (!next) return;
        setConfirmation((prev) => (
          prev?.type === 'success' ? { ...prev, order: { ...prev.order, ...next } } : prev
        ));
      } catch {
        // keep last known status
      }
    };

    const interval = setInterval(() => {
      if (Date.now() - startedAt > 20000) {
        clearInterval(interval);
        return;
      }
      poll();
    }, 1500);
    poll();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [confirmation?.type, confirmation?.order?.id, confirmation?.order?.status, confirmation?.order?.venue]);

  const tradeTotal = calculateTotal();
  const isModal = variant === 'modal';
  const venueLabel =
    executionVenue === 'schwab'
      ? `Schwab ${schwabAccountNumber ? maskAccount(schwabAccountNumber) : ''}`
      : 'Alpaca';

  const inputClass = 'w-full h-11 px-2.5 text-xs num lt-input';
  const labelClass = 'lt-label mb-1.5 block';

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="rounded-md border border-hairline bg-canvas px-2.5 py-2 flex flex-wrap items-center gap-2">
          <span className="lt-label shrink-0">Execute on</span>
          <select
            value={executionVenue}
            onChange={(e) => {
              const next = e.target.value === 'schwab' ? 'schwab' : 'alpaca';
              setExecutionVenue(next);
              try {
                localStorage.setItem(VENUE_STORAGE_KEY, next);
              } catch {
                /* ignore */
              }
            }}
            className="h-8 px-2 text-[11px] lt-input min-w-[110px]"
            title="Where this order is sent"
          >
            <option value="alpaca">Alpaca</option>
            <option value="schwab">Schwab</option>
          </select>
          {executionVenue === 'schwab' && (
            <select
              value={schwabAccountNumber}
              onChange={(e) => {
                const next = e.target.value;
                setSchwabAccountNumber(next);
                try {
                  localStorage.setItem(SCHWAB_ACCOUNT_STORAGE_KEY, next);
                } catch {
                  /* ignore */
                }
              }}
              disabled={schwabAccountsLoading || !schwabConnected || schwabAccounts.length === 0}
              className="h-8 px-2 text-[11px] lt-input min-w-[140px] flex-1"
              title={
                !schwabConnected
                  ? 'Connect Schwab on the Schwab page first'
                  : 'Schwab account for this order'
              }
            >
              {!schwabConnected && <option value="">Not connected — open Schwab page</option>}
              {schwabConnected && schwabAccounts.length === 0 && (
                <option value="">
                  {schwabAccountsLoading ? 'Loading accounts…' : 'No accounts'}
                </option>
              )}
              {schwabAccounts.map((a) => (
                <option key={a.accountNumber} value={a.accountNumber}>
                  {(a.accountType || 'Account')} {maskAccount(a.accountNumber)}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="rounded-md border border-hairline bg-canvas px-2.5 py-2 flex flex-wrap items-center gap-2">
          <span className="lt-label shrink-0">Preset</span>
          <select
            value={activeId || ''}
            onChange={(e) => {
              const id = e.target.value;
              setActiveId(id);
              const preset = presets.find((p) => p.id === id);
              applyPreset(preset);
            }}
            className="h-8 px-2 text-[11px] lt-input min-w-[140px] flex-1"
          >
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.limitOffset != null ? ` · mid${p.limitOffset >= 0 ? '+' : ''}${p.limitOffset}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              setPresetNameDraft(activePreset?.name || '');
              setShowSavePreset(true);
            }}
            className="h-8 px-2 rounded-md border border-hairline text-[11px] text-muted hover:bg-elevated"
            title="Save current ticket as preset"
          >
            Save
          </button>
          {activeId && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Delete this preset?')) removePreset(activeId);
              }}
              className="h-8 px-2 rounded-md border border-hairline text-[11px] text-muted hover:text-bear hover:bg-elevated"
              title="Delete active preset"
            >
              Del
            </button>
          )}
        </div>

        {showSavePreset && (
          <div className="rounded-md border border-hairline bg-surface px-2.5 py-2 space-y-2">
            <div className="lt-label">Save order preset</div>
            <input
              value={presetNameDraft}
              onChange={(e) => setPresetNameDraft(e.target.value)}
              className="w-full h-9 px-2 text-xs lt-input"
              placeholder="Name (e.g. Limit mid−0.05)"
            />
            <div className="grid grid-cols-2 gap-2 text-[10px] text-muted">
              <div>
                Size:{' '}
                <span className="num text-ink">
                  {orderBy === 'notional' ? `$${notional || '—'}` : `${qty || '—'} sh`}
                </span>
              </div>
              <div>
                Type: <span className="text-ink uppercase">{orderType}</span> · TIF{' '}
                <span className="text-ink uppercase">{timeInForce}</span>
              </div>
              <div>
                Limit offset:{' '}
                <input
                  type="number"
                  step="0.01"
                  className="w-20 h-7 px-1 text-[11px] num lt-input inline-block"
                  defaultValue={activePreset?.limitOffset ?? ''}
                  id="preset-limit-offset"
                  placeholder="e.g. -0.05"
                />
              </div>
              <div>Ext hours: {extendedHours ? 'yes' : 'no'}</div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowSavePreset(false)}
                className="flex-1 h-8 rounded-md border border-hairline text-[11px] text-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const name = presetNameDraft.trim() || 'Custom preset';
                  const offsetEl = document.getElementById('preset-limit-offset');
                  const offsetRaw = offsetEl?.value;
                  const limitOffset =
                    offsetRaw === '' || offsetRaw == null
                      ? null
                      : Number(offsetRaw);
                  savePreset({
                    id: activePreset?.name === name ? activePreset.id : `preset-${Date.now()}`,
                    name,
                    orderBy,
                    qty: orderBy === 'qty' && qty ? Number(qty) : null,
                    notional: orderBy === 'notional' && notional ? Number(notional) : null,
                    orderType,
                    timeInForce,
                    limitOffset: Number.isFinite(limitOffset) ? limitOffset : null,
                    extendedHours,
                  });
                  setShowSavePreset(false);
                }}
                className="flex-1 h-8 rounded-md bg-action text-white text-[11px] font-semibold"
              >
                Save preset
              </button>
            </div>
          </div>
        )}

        {message && (
          <div className={`px-2.5 py-2 rounded-md text-xs ${
            message.type === 'success'
              ? 'bg-[var(--lt-bull-dim)] text-bull border border-[rgba(61,220,151,0.28)]'
              : 'bg-[var(--lt-bear-dim)] text-bear border border-[rgba(240,113,103,0.28)]'
          }`}>
            {message.text}
          </div>
        )}

        {symbol && symbol.length >= 1 && !hideQuoteStrip && (
          <div className="rounded-md border border-hairline bg-canvas px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                {loadingQuote ? (
                  <div className="flex items-center gap-2 text-xs text-muted">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    <span>Loading quote...</span>
                  </div>
                ) : quote ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-muted">Bid</span>
                      <span className="ml-1.5 num font-medium text-bear">{formatCurrency(quote.bidPrice)}</span>
                    </div>
                    <div>
                      <span className="text-muted">Ask</span>
                      <span className="ml-1.5 num font-medium text-bull">{formatCurrency(quote.askPrice)}</span>
                    </div>
                    <div>
                      <span className="text-muted">Mid</span>
                      <span className="ml-1.5 num font-medium text-ink">{formatCurrency(midPrice)}</span>
                    </div>
                    <div>
                      <span className="text-muted">Spread</span>
                      <span className="ml-1.5 num font-medium text-ink">{formatCurrency(spread)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted">Enter a valid symbol to see quote</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`h-11 px-3 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                  autoRefresh
                    ? 'bg-bull text-[#0B0E14]'
                    : 'border border-hairline text-muted hover:bg-elevated'
                }`}
                title={autoRefresh ? 'Auto-refresh ON (1s)' : 'Auto-refresh OFF'}
              >
                <FontAwesomeIcon
                  icon={autoRefresh ? faSync : faPause}
                  className={autoRefresh ? 'animate-spin' : ''}
                />
                <span className="hidden sm:inline">{autoRefresh ? 'Live' : 'Paused'}</span>
              </button>
            </div>
          </div>
        )}

        <div className={isModal ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 lg:grid-cols-8 gap-2'}>
          {!isModal && (
          <div>
            <label className={labelClass}>Symbol</label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="AAPL"
              className={`${inputClass}${lockSymbol ? ' opacity-80' : ''}`}
              required
              readOnly={lockSymbol}
            />
          </div>
          )}

          {!isModal && (
          <div className="col-span-2">
            <label className={labelClass}>Side</label>
            <div className="grid grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setSide('buy')}
                className={`h-11 rounded-md text-xs font-semibold uppercase tracking-wider ${
                  side === 'buy'
                    ? 'bg-bull text-[#0B0E14]'
                    : 'border border-hairline text-muted hover:bg-elevated'
                }`}
              >
                Buy
              </button>
              <button
                type="button"
                onClick={() => setSide('sell')}
                className={`h-11 rounded-md text-xs font-semibold uppercase tracking-wider ${
                  side === 'sell'
                    ? 'bg-bear text-white'
                    : 'border border-hairline text-muted hover:bg-elevated'
                }`}
              >
                Sell
              </button>
            </div>
          </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="lt-label">{orderBy === 'qty' ? 'Shares' : 'Amount'}</label>
              <select
                value={orderBy}
                onChange={(e) => {
                  setOrderBy(e.target.value);
                  setQty('');
                  setNotional('');
                }}
                className="h-5 px-1 text-[10px] lt-input"
                disabled={executionVenue === 'schwab'}
                title={
                  executionVenue === 'schwab'
                    ? 'Schwab equity orders use share quantity'
                    : undefined
                }
              >
                <option value="qty">Qty</option>
                <option value="notional" disabled={executionVenue === 'schwab'}>
                  $
                </option>
              </select>
            </div>
            {orderBy === 'qty' ? (
              <input
                type="number"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="10"
                step="0.0001"
                min="0"
                className={inputClass}
                required={orderBy === 'qty'}
              />
            ) : (
              <input
                type="number"
                value={notional}
                onChange={(e) => setNotional(e.target.value)}
                placeholder="100.00"
                step="0.01"
                min="0"
                className={inputClass}
                required={orderBy === 'notional'}
              />
            )}
          </div>

          <div>
            <label className={labelClass}>Type</label>
            <select
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
              className={inputClass}
            >
              <option value="market">Market</option>
              <option value="limit">Limit</option>
              <option value="stop">Stop</option>
              <option value="stop_limit">Stop Limit</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>TIF</label>
            <select
              value={timeInForce}
              onChange={(e) => setTimeInForce(e.target.value)}
              className={inputClass}
            >
              <option value="day">Day</option>
              <option value="gtc">GTC</option>
              <option value="ioc">IOC</option>
              <option value="fok">FOK</option>
            </select>
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer h-11 px-2 rounded-md border border-hairline w-full">
              <input
                type="checkbox"
                checked={extendedHours}
                onChange={(e) => setExtendedHours(e.target.checked)}
                className="w-3.5 h-3.5 accent-[var(--lt-action)]"
              />
              <span className="text-[11px] font-medium text-muted whitespace-nowrap">Ext. hours</span>
            </label>
          </div>

          {!isModal && (
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className={`w-full h-11 rounded-md text-xs font-semibold uppercase tracking-wider transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${
                side === 'buy'
                  ? 'bg-bull text-[#0B0E14]'
                  : 'bg-bear text-white'
              }`}
              title={`Submit on ${venueLabel}`}
            >
              {loading
                ? '...'
                : `${side === 'buy' ? 'Buy' : 'Sell'} ${symbol || ''} · ${
                    executionVenue === 'schwab' ? 'Schwab' : 'Alpaca'
                  }`.trim()}
            </button>
          </div>
          )}
        </div>

        {tradeTotal && (
          <div className="rounded-md border border-hairline bg-canvas px-3 py-2">
            <div className="lt-label mb-1.5">Preview</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <span className="text-muted">Cost basis</span>
                <span className="ml-1.5 num font-medium text-ink">{formatCurrency(tradeTotal.subtotal)}</span>
              </div>
              <div>
                <span className="text-muted">Est. fees</span>
                <span className="ml-1.5 num font-medium text-ink">{formatCurrency(tradeTotal.fee)}</span>
              </div>
              <div className="col-span-2 flex items-center justify-between sm:justify-start sm:gap-2">
                <span className="text-muted">Est. {side === 'buy' ? 'cost' : 'proceeds'}</span>
                <span className={`num font-semibold ${side === 'buy' ? 'text-bull' : 'text-bear'}`}>
                  {formatCurrency(tradeTotal.total)}
                </span>
              </div>
            </div>
          </div>
        )}

        {((orderType === 'limit' || orderType === 'stop_limit') || (orderType === 'stop' || orderType === 'stop_limit')) && (
          <div className="grid grid-cols-2 gap-2">
            {(orderType === 'limit' || orderType === 'stop_limit') && (
              <div>
                <label className={labelClass}>Limit price</label>
                <input
                  type="number"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                  placeholder="150.00"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  required={orderType === 'limit' || orderType === 'stop_limit'}
                />
              </div>
            )}

            {(orderType === 'stop' || orderType === 'stop_limit') && (
              <div>
                <label className={labelClass}>Stop price</label>
                <input
                  type="number"
                  value={stopPrice}
                  onChange={(e) => setStopPrice(e.target.value)}
                  placeholder="145.00"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  required={orderType === 'stop' || orderType === 'stop_limit'}
                />
              </div>
            )}
          </div>
        )}

        {isModal && (
          <button
            type="submit"
            disabled={loading}
            className={`w-full h-12 rounded-md text-sm font-semibold uppercase tracking-[0.12em] transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${
              side === 'buy'
                ? 'bg-bull text-[#0B0E14]'
                : 'bg-bear text-white'
            }`}
            title={`Submit on ${venueLabel}`}
          >
            {loading
              ? '...'
              : `${side === 'buy' ? 'Buy' : 'Sell'} ${symbol || ''} · ${
                  executionVenue === 'schwab' ? 'Schwab' : 'Alpaca'
                }`.trim()}
          </button>
        )}
      </form>

      {riskPrompt && createPortal(
        <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <button
            type="button"
            className="absolute inset-0 bg-[#0B0E14]/75"
            aria-label="Dismiss risk warning"
            onClick={() => setRiskPrompt(null)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="risk-warn-title"
            className="relative w-full sm:max-w-[440px] rounded-t-xl sm:rounded-xl border border-[rgba(232,184,74,0.4)] bg-surface shadow-[0_24px_80px_rgba(0,0,0,0.45)]"
          >
            <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-hairline">
              <div className="flex items-start gap-2 min-w-0">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-[var(--lt-warning)] mt-0.5" />
                <div>
                  <div className="lt-label mb-1">Risk warning</div>
                  <h3 id="risk-warn-title" className="text-base font-semibold text-ink leading-none">
                    Confirm before submit
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRiskPrompt(null)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>
            <div className="px-4 py-3 space-y-2">
              <ul className="space-y-1.5">
                {riskPrompt.warnings.map((warning) => (
                  <li
                    key={warning}
                    className="text-xs text-ink rounded-md border border-hairline bg-canvas px-2.5 py-2 flex gap-2"
                  >
                    <FontAwesomeIcon icon={faShieldAlt} className="text-action text-[10px] mt-0.5" />
                    <span>{warning}</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setRiskPrompt(null)}
                  className="flex-1 h-10 rounded-md border border-hairline text-xs font-semibold text-muted hover:bg-elevated"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={async () => {
                    const payload = riskPrompt.orderData;
                    setRiskPrompt(null);
                    await placeOrder(payload);
                  }}
                  className="flex-1 h-10 rounded-md bg-action text-white text-xs font-semibold disabled:opacity-50"
                >
                  {loading ? 'Submitting…' : 'Submit anyway'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {confirmation && createPortal(
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <button
            type="button"
            className="absolute inset-0 bg-[#0B0E14]/75"
            aria-label="Close confirmation"
            onClick={() => setConfirmation(null)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-confirm-title"
            className="relative w-full sm:max-w-[440px] rounded-t-xl sm:rounded-xl border border-hairline bg-surface shadow-[0_24px_80px_rgba(0,0,0,0.45)]"
          >
            <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-hairline">
              <div>
                <div className="lt-label mb-1">
                  {confirmation.type === 'success' ? 'Order confirmation' : 'Order failed'}
                </div>
                <h3 id="order-confirm-title" className="text-base font-semibold text-ink leading-none">
                  {confirmation.type === 'success'
                    ? `${(confirmation.order?.side || '').toUpperCase()} ${confirmation.order?.symbol || ''}`
                    : 'Could not place order'}
                </h3>
                {confirmation.type === 'success' && confirmation.order?.venue && (
                  <div className="text-[11px] text-muted mt-1.5">
                    Via{' '}
                    <span className="text-ink">
                      {confirmation.order.venue === 'schwab'
                        ? `Schwab ${maskAccount(confirmation.order.accountNumber)}`
                        : 'Alpaca'}
                    </span>
                    {confirmation.order.id != null && (
                      <span className="num"> · #{confirmation.order.id}</span>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmation(null)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>

            <div className="px-4 py-4 space-y-3">
              {confirmation.type === 'error' ? (
                <div className="rounded-md border border-[rgba(240,113,103,0.28)] bg-[var(--lt-bear-dim)] px-3 py-2 text-sm text-bear">
                  {confirmation.text}
                </div>
              ) : (
                <>
                  {(() => {
                    const order = confirmation.order || {};
                    const status = String(order.status || 'submitted').toLowerCase();
                    const statusColor = status === 'filled'
                      ? 'text-bull bg-[var(--lt-bull-dim)]'
                      : ['rejected', 'canceled', 'cancelled', 'expired'].includes(status)
                        ? 'text-bear bg-[var(--lt-bear-dim)]'
                        : 'text-[var(--lt-warning)] bg-[rgba(232,184,74,0.12)]';
                    return (
                      <div className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider ${statusColor}`}>
                        {status === 'filled' && <FontAwesomeIcon icon={faCheck} />}
                        {!TERMINAL_STATUSES.has(status) && order.venue !== 'schwab' && (
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        )}
                        {formatStatus(order.status || 'submitted')}
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-md border border-hairline bg-canvas p-3">
                      <div className="lt-label mb-1">Symbol</div>
                      <div className="num font-semibold text-ink">{confirmation.order?.symbol || '—'}</div>
                    </div>
                    <div className="rounded-md border border-hairline bg-canvas p-3">
                      <div className="lt-label mb-1">Side</div>
                      <div className={`num font-semibold uppercase ${confirmation.order?.side === 'sell' ? 'text-bear' : 'text-bull'}`}>
                        {confirmation.order?.side || '—'}
                      </div>
                    </div>
                    <div className="rounded-md border border-hairline bg-canvas p-3">
                      <div className="lt-label mb-1">Qty</div>
                      <div className="num font-semibold text-ink">
                        {confirmation.order?.qty != null && !Number.isNaN(confirmation.order.qty)
                          ? confirmation.order.qty
                          : confirmation.order?.notional != null
                            ? formatCurrency(confirmation.order.notional)
                            : '—'}
                      </div>
                    </div>
                    <div className="rounded-md border border-hairline bg-canvas p-3">
                      <div className="lt-label mb-1">Type</div>
                      <div className="num font-semibold text-ink uppercase">{confirmation.order?.type || '—'}</div>
                    </div>
                    <div className="rounded-md border border-hairline bg-canvas p-3">
                      <div className="lt-label mb-1">Filled</div>
                      <div className="num font-semibold text-ink">
                        {confirmation.order?.filledQty || 0}
                        {confirmation.order?.filledAvgPrice
                          ? ` @ ${formatCurrency(confirmation.order.filledAvgPrice)}`
                          : ''}
                      </div>
                    </div>
                    <div className="rounded-md border border-hairline bg-canvas p-3">
                      <div className="lt-label mb-1">TIF</div>
                      <div className="num font-semibold text-ink uppercase">{confirmation.order?.timeInForce || '—'}</div>
                    </div>
                  </div>

                  {confirmation.order?.id && (
                    <div className="text-[11px] text-muted break-all">
                      Order ID {confirmation.order.id}
                    </div>
                  )}
                </>
              )}

              <button
                type="button"
                onClick={() => setConfirmation(null)}
                className="w-full h-11 rounded-md bg-action text-white text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OrderForm;
